import React, { useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { AWS_CLOUD_FRONT_URL } from "../../lib/constants";

const HomepageFooter: React.FC = () => {
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeInUp");
            entry.target.classList.remove("opacity-0");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={footerRef}
      className="mx-[5vw] lg:mx-[15vw] opacity-0 transition-all duration-700"
    >
      <div className="border-t border-gray-300 pt-10">
        {/* Main content wrapper */}
        <div className="flex justify-between mb-1 animate-fadeIn">
          {/* Left Section */}
          <div className="flex flex-col animate-slideUp">
            <Link
              to="/"
              className="transition-transform duration-300 hover:scale-105"
            >
              <img
                src="/Images/LoginPageImages/Auth-Page-Logo.png"
                className="w-32 mb-6"
                alt="homepage_logo"
              />
            </Link>

            <p className="text-customGrayDark text-sm font-medium mb-4">
              Get our app!
            </p>
            <div className="flex gap-3 mb-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.afrocart.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300 hover:transform hover:scale-110"
              >
                <img
                  src={`${AWS_CLOUD_FRONT_URL}/PlayStoreBadge.png`}
                  alt="Play store download button"
                  className="h-10 w-auto rounded-md"
                />
              </a>
              <a
                href="https://apps.apple.com/us/app/afrocart/id6636529260"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300 hover:transform hover:scale-110"
              >
                <img
                  src={`${AWS_CLOUD_FRONT_URL}/AppStoreBadge.png`}
                  alt="App store download button"
                  className="h-10 w-auto rounded-md w-[120px] h-[40px]"
                />
              </a>
            </div>

            <a
              href="mailto:admin@afrocart.ca"
              className="text-customGreenColor text-lg hover:underline text-[24px]"
            >
              admin@afrocart.ca
            </a>
          </div>

          {/* Right Section - Updated layout */}
          <div className="flex gap-32 w-1/2 animate-slideUp">
            {/* Company Links */}
            <div className="flex flex-col">
              <h3 className="font-bold text-gray-800 mb-4">Company</h3>
              <div className="flex flex-col space-y-3 text-[14px]">
                <NavLink
                  to="/about"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                >
                  About
                </NavLink>
                <NavLink
                  to="/advantages"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                >
                  Advantages
                </NavLink>
                <NavLink
                  to="/ourVendors"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                >
                  Vendors
                </NavLink>
                <NavLink
                  to="/contact-us"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                >
                  Contact Us
                </NavLink>
              </div>
            </div>

            {/* Follow us Links */}
            <div className="flex flex-col ml-20">
              <h3 className="font-bold text-gray-800 mb-4">Follow us</h3>
              <div className="flex flex-col space-y-3 text-[14px]">
                <a
                  href="https://www.instagram.com/afrocart.ca"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
                <a
                  href="https://x.com/afrocart"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
                <a
                  href="https://www.facebook.com/afrocart.ca"
                  className="text-gray-600 hover:text-customGreenColor transform transition-all duration-300 hover:translate-x-2 hover:scale-105 w-fit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </div>
            </div>

            {/* Get the App Links */}
            {/* <div className="flex flex-col">
              <h3 className="font-bold text-gray-800 mb-4">Get the App</h3>
              <div className="flex flex-col space-y-3">
                <NavLink
                  to="/about"
                  className="text-gray-600 hover:text-customGreenColor"
                >
                  About
                </NavLink>
                <NavLink
                  to="/advantages"
                  className="text-gray-600 hover:text-customGreenColor"
                >
                  Advantages
                </NavLink>
                <NavLink
                  to="/credentials"
                  className="text-gray-600 hover:text-customGreenColor"
                >
                  Credentials
                </NavLink>
                <NavLink
                  to="/contact-us"
                  className="text-gray-600 hover:text-customGreenColor"
                >
                  Contact Us
                </NavLink>
              </div>
            </div> */}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex border-gray-200 animate-fadeIn">
          {/* Copyright on left side */}
          <p className="text-gray-500 flex-start text-sm w-1/2">
            © Copyright@{new Date().getFullYear()} Afrocart. All rights reserved
          </p>

          {/* Bottom Links with exact spacing */}
          <div className="flex gap-8 text-sm text-gray-500 w-1/2 text-[14px]">
            <NavLink
              to="/credentials"
              className="hover:text-customGreenColor transform transition-all duration-300 hover:-translate-y-1 hover:scale-105"
            >
              Credentials
            </NavLink>

            <NavLink
              to="/terms-of-use"
              className="hover:text-customGreenColor transform transition-all duration-300 hover:-translate-y-1 hover:scale-105"
            >
              Terms & Conditions
            </NavLink>
            <NavLink
              to="/privacy-policy"
              className="hover:text-customGreenColor transform transition-all duration-300 hover:-translate-y-1 hover:scale-105"
            >
              Privacy Policy
            </NavLink>
            <NavLink
              to="/imprint-data-protection"
              className="hover:text-customGreenColor transform transition-all duration-300 hover:-translate-y-1 hover:scale-105"
            >
              Imprint Data Protection
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageFooter;
